import MarkdownIt from 'markdown-it'
import createDOMPurify from 'dompurify'

// DOMPurify's default export is an instance in the browser but may be a
// factory function in Node/jsdom (vitest).  Handle both shapes so tests
// work regardless of how the module is resolved.
const DOMPurify: { sanitize: typeof createDOMPurify.sanitize } =
  typeof createDOMPurify.sanitize === 'function'
    ? createDOMPurify
    : (createDOMPurify as unknown as (window: Window) => typeof createDOMPurify)(window)

/**
 * Preconfigured markdown-it instance for rendering AI chat responses.
 *
 * Security: linkify is disabled and html is off (the default) so raw HTML
 * in the AI output is escaped by the parser itself. DOMPurify is applied
 * as a second layer after rendering.
 */
const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: false,
  typographer: false
})

// Disable images — not needed and avoids data-URI / tracking-pixel vectors.
md.disable('image')

/**
 * Allowed HTML tags and attributes after DOMPurify sanitisation.
 * Keep the allow-list minimal to reduce XSS surface.
 */
const PURIFY_CONFIG: createDOMPurify.Config = {
  ALLOWED_TAGS: [
    // inline
    'strong',
    'em',
    'code',
    's',
    'br',
    // block
    'p',
    'ul',
    'ol',
    'li',
    'blockquote',
    'pre',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    // tables
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td'
  ],
  ALLOWED_ATTR: ['class']
}

/**
 * Render a markdown string to sanitised HTML, safe for use with `v-html`.
 *
 * Pipeline:
 *   raw markdown ─▶ markdown-it (html:false) ─▶ DOMPurify ─▶ safe HTML
 *
 * @param text - Raw markdown text (e.g. from an AI response)
 * @returns Sanitised HTML string
 */
export function renderMarkdown(text: string): string {
  const rawHtml = md.render(text)
  return DOMPurify.sanitize(rawHtml, PURIFY_CONFIG)
}
